import { useState, useContext, createContext, useEffect } from 'react';
import { clsx } from '@wonderful/wwc/dist/src/helpers/clsx';
import { isPreviewing } from '@builder.io/sdk-react';
// Import Swiper
import { Swiper, SwiperSlide, type SwiperClass } from 'swiper/react';
import { type SwiperOptions } from 'swiper/types/swiper-options';
import { swiperInit } from '@/helpers/swiper';
swiperInit();
import { handleRedirectClick } from '@/helpers/utils';
import { useTranslation } from '@/app/i18n/TranslationContext';
import Filter from '@/components/modules/Filter/Filter';
import Popup from '@/components/modules/Popup/Popup';
import Section from '@/components/design/Section/Section';
import Button from '@/components/common/Button/Button';
import {
  FilterableWhereToBuyProps,
  FilterableWhereToBuyContextTypes,
  Store,
  FilteredStoreProps,
} from './FilterableWhereToBuy.types';
import styles from './FilterableWhereToBuy.module.scss';

import { Breakpoints } from '@/helpers/constants';
import { useIsRtl } from '@/helpers/utils';
import { BuilderEditingHelper } from '@wonderful/wwc/dist/src/common/BuilderEditingHelper/BuilderEditingHelper';
import BuilderImage from '@/components/common/BuilderImage/BuilderImage';

// Context for storing component state
const FilterableWhereToBuyContext = createContext<
  FilterableWhereToBuyContextTypes | undefined
>(undefined);

const useFilterableWhereToBuyContext = () => {
  const context = useContext(FilterableWhereToBuyContext);
  if (context === undefined) {
    throw new Error(
      'useFilterableWhereToBuyContext must be used within a FilterableWhereToBuyContext.Provider'
    );
  }
  return context;
};

export default function FilterableWhereToBuy(props: FilterableWhereToBuyProps) {
  const { countries, isFilterEnabled, defaultFilter, builderBlock } = props;
  const componentId = builderBlock?.id;

  const { t } = useTranslation();
  const isVisible = !countries;

  // Sorting countries by title
  countries?.sort((a, b) => {
    let x = a?.title?.toLowerCase();
    let y = b?.title?.toLowerCase();
    return x?.localeCompare(y); // Used to compare accented strings
  });

  // Check if country has a title to add as a filter tag
  const filterTags = countries?.reduce((result: string[], country) => {
    if (country.title) {
      result.push(country.title?.toLowerCase());
    }
    return result;
  }, []);

  // For leaving site popup
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [redirectLink, setRedirectLink] = useState('');
  const popupProps = {
    label: t('We’ll miss you!'),
    description: t(
      'We’re gettin’ crackin’ on redirecting you. Do you want to continue?'
    ),
    isPopupOpen: isPopupOpen,
    setIsPopupOpen: setIsPopupOpen,
    redirectLink: redirectLink,
    setRedirectLink: setRedirectLink,
    cancelBtnText: t('I’ll stay'),
    acceptBtnText: t('Gotta pistachi-go!'),
  };

  const localizedStrings = {
    all: t('all').toLowerCase(),
    buyOnline: t('Buy Online'),
  };

  const [activeFilter, setActiveFilter] = useState(
    defaultFilter ? defaultFilter?.toLowerCase() : localizedStrings.all
  );

  // EMEAWP-309 - So builder UI will update on defaultFilter change
  useEffect(() => {
    async function setPreviewFilter() {
      if (defaultFilter) {
        setActiveFilter(defaultFilter?.toLowerCase());
      }
    }

    isPreviewing() && setPreviewFilter();
  }, [defaultFilter]);

  return (
    <Section
      className={clsx('container', styles.whereToBuySection)}
      data-testid="filterable-wtb"
    >
      <BuilderEditingHelper
        componentName="FilterableWhereToBuy"
        visible={isVisible}
      />
      {isPopupOpen && <Popup {...popupProps} />}

      <div className="row">
        <div className="col-12">
          <FilterableWhereToBuyContext.Provider
            value={{
              popupProps: popupProps,
              localizedStrings: localizedStrings,
              activeFilter: activeFilter,
            }}
          >
            {isFilterEnabled && (
              <Filter
                tags={filterTags}
                currentFilter={activeFilter}
                componentId={componentId}
                classes={styles.whereToBuyFilter}
                hasAll={false}
                ariaLabel="Country filters"
                setActiveFilter={setActiveFilter}
                data-testid={'wtb-filter'}
              />
            )}
            <div id={componentId} data-testid="wtb-country-stores">
              {countries?.map(country => {
                const countryTitle = country.title?.toLowerCase();
                const countryId = `${componentId}-${countryTitle}`.replace(
                  /\s+/g,
                  ''
                );
                // Creates carousel if country has more than one store
                // If carousel has 3 or 2 stores, only create carousel in mobile
                // If country has one store, just render a single store item
                if (country?.stores?.length > 1) {
                  return (
                    <FilteredStoreCarousel
                      key={countryId}
                      id={countryId}
                      country={country}
                      tag={countryTitle}
                    />
                  );
                } else if (country?.stores?.length === 1) {
                  return (
                    <FilteredStore
                      key={countryId}
                      id={countryId}
                      country={country}
                      tag={countryTitle}
                    />
                  );
                }
              })}
            </div>
          </FilterableWhereToBuyContext.Provider>
        </div>
      </div>
    </Section>
  );
}

function FilteredStoreCarousel(props: FilteredStoreProps) {
  const { id, country, tag } = props;
  const { activeFilter, localizedStrings } = useFilterableWhereToBuyContext();

  const [isInViewport, setIsInViewport] = useState(false);

  const isRtl = useIsRtl();
  const [slidesPerViewValue, setSlidesPerViewValue] = useState(1);
  const [nextSwiperIndex, setNextSwiperIndex] = useState(1);

  const onEnterViewport = () => {
    setIsInViewport(true);
  };

  const isHidden =
    tag !== activeFilter && activeFilter !== localizedStrings.all;

  const numberOfStores = country?.stores?.length;

  return (
    <Section
      className={clsx(
        styles.storesContainer,
        isHidden && styles.filterListItemHidden,
        isInViewport ? styles.inViewport : ''
      )}
      onEnterViewport={onEnterViewport}
      aria-hidden={isHidden}
    >
      <button
        className={clsx(
          `swiper-button-prev-${id}`,
          numberOfStores <= 3 && styles.hiddenMd
        )}
      ></button>
      <button
        className={clsx(
          `swiper-button-next-${id}`,
          numberOfStores <= 3 && styles.hiddenMd
        )}
      ></button>
      <h2 className={styles.countryTitle}>{country.title}</h2>
      <Swiper
        navigation={{
          nextEl: `.swiper-button-next-${id}`,
          prevEl: `.swiper-button-prev-${id}`,
        }}
        slidesPerView={slidesPerViewValue}
        slidesPerGroup={1}
        pagination={{
          clickable: true,
        }}
        a11y={{
          enabled: true,
        }}
        breakpoints={{
          [Breakpoints.md]: {
            slidesPerView: numberOfStores < 3 ? numberOfStores : 3,
          },
        }}
        className={clsx(styles.storeWrapper, styles.swiper)}
        dir={isRtl ? 'rtl' : 'ltr'}
        // EMEAWP-343 - Added logic to not lazy load next swiper image
        onBreakpoint={(swiper: SwiperClass, breakpoint: SwiperOptions) => {
          setSlidesPerViewValue(Number(breakpoint.slidesPerView));
          setNextSwiperIndex(
            swiper.activeIndex + Number(breakpoint.slidesPerView)
          );
        }}
        onSlideChange={(swiper: SwiperClass) => {
          setNextSwiperIndex(swiper.activeIndex + slidesPerViewValue);
        }}
      >
        {country?.stores?.map((store, index) => {
          const storeId = `${id}-${country.title}-${index}`;
          return (
            <SwiperSlide
              key={storeId}
              className={styles.storeItem}
              data-testid="store-item"
            >
              <FilteredItem
                store={store}
                index={index}
                nextIndex={nextSwiperIndex}
              />
            </SwiperSlide>
          );
        })}
        <strong className={styles.buyOnline}>
          {localizedStrings.buyOnline}
        </strong>
      </Swiper>
    </Section>
  );
}

function FilteredStore(props: FilteredStoreProps) {
  const { id, country, tag } = props;
  const { activeFilter, localizedStrings } = useFilterableWhereToBuyContext();

  const [isInViewport, setIsInViewport] = useState(false);

  const onEnterViewport = () => {
    setIsInViewport(true);
  };

  const isHidden =
    tag !== activeFilter && activeFilter !== localizedStrings.all;

  return (
    <Section
      className={clsx(
        styles.storesContainer,
        styles.filteredStore,
        isHidden && styles.filterListItemHidden,
        isInViewport ? styles.inViewport : ''
      )}
      onEnterViewport={onEnterViewport}
      aria-hidden={isHidden}
    >
      <h2 className={styles.countryTitle}>{country.title}</h2>
      <div className={styles.storeWrapper}>
        {country.stores.map((store, index) => {
          const storeId = `${id}-${country.title}-${index}`;
          return (
            <div
              key={storeId}
              className={styles.storeItem}
              data-testid="store-item"
            >
              {/*
                EMEAWP-343 - Set nextIndex to 1 here since we want to lazy load first item (index: 0) if only one store.
                If set to 0, will not lazy load first image.
                */}
              <FilteredItem store={store} index={index} nextIndex={1} />
            </div>
          );
        })}
        <strong className={styles.buyOnline}>
          {localizedStrings.buyOnline}
        </strong>
      </div>
    </Section>
  );
}

function FilteredItem(props: {
  store: Store;
  index: Number;
  nextIndex: Number;
}) {
  const { store, index, nextIndex } = props;
  const { popupProps, localizedStrings } = useFilterableWhereToBuyContext();

  const button = store.button;
  return (
    <div className={styles.storeInfoContainer}>
      <div className={styles.storeImageWrapper}>
        {button?.href ? (
          <a
            className={styles.storeImageInner}
            href={button.href}
            tabIndex={-1}
            onClick={event =>
              handleRedirectClick(
                button.href,
                popupProps.isPopupOpen,
                popupProps.setIsPopupOpen,
                popupProps.setRedirectLink,
                event
              )
            }
          >
            <BuilderImage
              imageSrc={store?.image?.src}
              alt=""
              mobileWidth={'160px'}
              tabletWidth={'180px'}
              desktopWidth={'220px'}
              classes={clsx(styles.storeImage)}
              // EMEAWP-343 - Added some logic to not lazy load next image in swiper
              disableLazyLoad={index === nextIndex}
            />
          </a>
        ) : (
          <div className={styles.storeImageInner}>
            <BuilderImage
              imageSrc={store?.image?.src}
              alt=""
              mobileWidth={'160px'}
              tabletWidth={'180px'}
              desktopWidth={'220px'}
              classes={clsx(styles.storeImage)}
              disableLazyLoad={index === nextIndex}
            />
          </div>
        )}
      </div>
      <div className={styles.storeItemInfo}>
        <h3>{store.title}</h3>
        {button?.text && button?.href ? (
          <Button
            as="a"
            href={button?.href}
            aria-label={`${localizedStrings.buyOnline}, ${store.title}`}
            onClick={event =>
              handleRedirectClick(
                button?.href,
                popupProps.isPopupOpen,
                popupProps.setIsPopupOpen,
                popupProps.setRedirectLink,
                event
              )
            }
            design={button?.design}
            className={styles.buyNowButton}
          >
            {button?.text}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
